import { type DocumentShortcutState, generateObjectId, SHORTCUT_LIST_FIELD, type SlateContents } from '@alpod-ai-writer/shared';
import { NodeApi, type Value } from '@udecode/plate';
import clsx from 'clsx';
import { Avatar, AvatarGroup, Button, Label, TextInput } from 'flowbite-react';
import { AlertTriangleIcon, PencilIcon, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Y from 'yjs';

import { useModals } from '../../hooks/useModals.hook';
import { getInitialsFromName } from '../../lib/identity.utils';
import { useDocumentEditor } from '../hooks/useDocumentEditor.hook';
import { useInlineEditor } from '../hooks/useInlineEditor.hook';
import { useInlineEditorStatic } from '../hooks/useInlineEditorStatic.hook';
import { getSectionLink, useSectionNav } from '../hooks/useSectionNav.hook';
import { type ShortcutRef,useShortcutRefs } from '../hooks/useShortcutRefs';
import { useYDocAwarenessFieldSelection } from '../hooks/useYDocAwernessSelection.hook';
import { useYDocMap } from '../hooks/useYDocText.hook';
import { limitString } from '../utils/string.utils';
import { InlineEditor } from './common/inlineEditor.component';
import { InlineEditorReadonly } from './common/inlineEditorReadonly.component';
import { ShortcutImportModal } from './common/shortcutImportModal.component';

const createSlateContents = (text: string): SlateContents => ({
    children: [
        { type: 'p', children: [ { text } ] }
    ]
});

export interface ShortcutListEditorProps {
    yDoc: Y.Doc;
    readOnly: boolean;
}

export const ShortcutListEditor: React.FC<ShortcutListEditorProps> = ({
    yDoc,
    readOnly
}) => {
    const { entries, addEntry, updateEntry, removeEntry } = useYDocMap<DocumentShortcutState>(yDoc, SHORTCUT_LIST_FIELD);
    const { shortcutRefs } = useShortcutRefs();
    const modals = useModals();

    const handleAddEntry = useCallback(() => {
        const id = generateObjectId();
        addEntry(id, {
            shortcut: 'Nová zkratka',
            meaning: null,
            content: createSlateContents(''),
        });
    }, [ addEntry ]);

    const handleUpdateEntry = useCallback((key: string, entry: DocumentShortcutState) => {
        updateEntry(key, entry);
    }, [ updateEntry ]);

    const handleRemoveEntry = useCallback((key: string) => {
        removeEntry(key);
    }, [ removeEntry ]);

    const handleImport = useCallback(() => {
        modals.showModal('shortcut-import', ShortcutImportModal, {
            onImport: (data) => {
                data.forEach((item) => {
                    const id = generateObjectId();
                    const { shortcut, meaning } = item;

                    addEntry(id, {
                        shortcut,
                        content: createSlateContents(meaning),
                    });
                });
            }
        });
    }, [ addEntry, modals ]);

    return (
        <div className='flex flex-col gap-6 px-1 md:px-0'>
            <div className='flex flex-col gap-2'>
                {entries.map(([ key, entry ]) => (
                    <ShortcutListEntry
                        key={key}
                        entryKey={key}
                        entry={entry}
                        refs={shortcutRefs.get(key) ?? []}
                        readOnly={readOnly}
                        onUpdate={handleUpdateEntry}
                        onRemove={handleRemoveEntry}
                    />
                ))}
                {!entries.length && (
                    <div className='text-gray-500 text-left border border-gray-300 text-sm p-4 rounded-lg'>Do dokumentu zatím nebyly vloženy žádné zkratky. Přidejte zkratku níže nebo ji rovnou vložte do textu napsáním znaku <code className='bg-gray-200 rounded p-0.5'>$</code>.</div>
                )}
            </div>
            {!readOnly && (
                <div className='flex flex-row items-center justify-end gap-2 px-4 md:px-0'>
                    <Button onClick={handleImport} color='primary'>Vložit hromadně</Button>
                    <Button onClick={handleAddEntry} color='primary'>Přidat položku</Button>
                </div>
            )}
        </div>
    );
};

const ShortcutListEntry: React.FC<{
    entryKey: string;
    entry: DocumentShortcutState;
    refs: ShortcutRef[];
    readOnly: boolean;
    onUpdate: (key: string, entry: DocumentShortcutState) => void;
    onRemove: (key: string) => void;
}> = ({
    entryKey,
    entry,
    refs,
    readOnly,
    onUpdate,
    onRemove
}) => {
    const docEditor = useDocumentEditor();
    const { currentSection } = useSectionNav(docEditor.template.config.sections);

    const {
        selectedBy,
        resetAwareness: resetAwerness,
        setAwarenessToField: setAwernessToField
    } = useYDocAwarenessFieldSelection(docEditor.yjsProvider, `${SHORTCUT_LIST_FIELD}.${entryKey}`);

    const [ isOpened, setIsOpened ] = useState(currentSection?.headingId === entryKey);
    const normalizedContent = (entry.content ?? createSlateContents(''));

    const toggleOpen = useCallback((ev: React.MouseEvent) => {
        ev.stopPropagation();

        setIsOpened((prev) => !prev);
    }, []);

    const handleRemove = useCallback(() => {
        onRemove(entryKey);
    }, [ onRemove, entryKey ]);

    const handleFocus = useCallback(() => {
        setAwernessToField();
    }, [ setAwernessToField, entryKey, isOpened ]);

    const handleBlur = useCallback(() => {
        resetAwerness();
    }, [ resetAwerness, entryKey ]);

    const editor = useInlineEditorStatic({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        value: normalizedContent.children as Value,
    });

    useEffect(() => {
        editor.tf.setValue(normalizedContent.children as Value);
    }, [ editor, entry.content ]);

    useEffect(() => {
        if (currentSection?.headingId === entryKey) {
            // Scroll to the entry when it's focused
            const targetEl = document.getElementById(`${currentSection.sectionId}/${entryKey}`);
            if (targetEl) {
                targetEl.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest'
                });
            }
        }
    }, []);

    useEffect(() => {
        return () => {
            // Reset awareness when unmounting to handle case when user didn't close the entry
            resetAwerness();
        };
    }, []);

    const selectionColor = selectedBy[0]?.color ?? undefined;
    const hasMeaningContents = NodeApi.isNode(normalizedContent) && NodeApi.string(normalizedContent).length > 0;

    return (
        <div className='relative'>
            <div
                className={clsx(
                    'w-full flex flex-col rounded-xl overflow-hidden shadow-[0px_0px_10px_transparent] border-2 transition-all duration-200',
                    isOpened && 'focus-within:shadow-primary-200 border-primary-400',
                    !isOpened && 'border-transparent'
                )}
                style={{
                    borderColor: selectionColor,
                }}
                onFocus={handleFocus}
                onBlur={handleBlur}
            >
                <div
                    className={clsx(
                        'flex flex-row items-start justify-start pl-5 pr-4 py-4 gap-1 cursor-pointer transition-colors duration-200 bg-white hover:bg-gray-200',
                    )}
                    onClick={toggleOpen}
                >
                    <a id={`${currentSection?.sectionId}/${entryKey}`} />
                    <button
                        className='flex flex-col grow items-start justify-start text-left gap-1'
                        onClick={toggleOpen}
                    >
                        <span className='block text-sm font-semibold'>{entry.shortcut || '(prázdná položka)'}</span>
                        {hasMeaningContents && (
                            <InlineEditorReadonly
                                editor={editor}
                                className='text-sm'
                            />
                        )}
                        {!hasMeaningContents && (
                            <span className='flex flex-row items-center gap-1 text-sm text-red-800 font-semibold'>
                                <AlertTriangleIcon size={16} /> <span>Význam zkratky není doplněn.</span>
                            </span>
                        )}
                        <span className='flex flex-row flex-wrap gap-2 pt-1'>
                            {refs.length > 0 && (
                                <>
                                    {refs.map((ref, index) => (
                                        <a
                                            key={index}
                                            href={getSectionLink(ref.sectionId, ref.headingId)}
                                            className='text-xs text-primary-500 underline hover:no-underline'
                                            onClick={(ev) => ev.stopPropagation()}
                                        >{ref.sectionTitle}{ref.headingTitle ? ' - ' + limitString(ref.headingTitle, 20) : ''}</a>
                                    ))}
                                </>
                            )}
                            {refs.length === 0 && (
                                <span className='flex flex-row items-center gap-1 text-xs text-gray-600'><AlertTriangleIcon size={14} /> Není použito nikde v dokumentu.</span>
                            )}
                        </span>
                    </button>
                    {!readOnly && (
                        <>
                            <button
                                className={clsx(
                                    'bg-transparent hover:bg-white hover:text-primary-500 rounded-md transition-colors duration-200 p-2',
                                    isOpened && 'text-primary-550'
                                )}
                                onClick={toggleOpen}
                            ><PencilIcon size={20} /></button>
                            <button
                                className='bg-transparent hover:bg-white hover:text-red-800 rounded-md transition-colors duration-200 p-2'
                                onClick={handleRemove}
                            ><TrashIcon size={20} /></button>
                        </>
                    )}
                </div>
                {isOpened && (
                    <ShortcutListEntryEdit
                        entryKey={entryKey}
                        entry={entry}
                        readOnly={readOnly}
                        onUpdate={onUpdate}
                    />
                )}
            </div>
            <AvatarGroup className='absolute right-2 bottom-0 translate-y-1/2 z-10'>
                {selectedBy?.map((cursor) => (
                    <Avatar
                        key={cursor.clientId}
                        alt={cursor.name}
                        title={cursor.name}
                        placeholderInitials={getInitialsFromName(cursor.name)}
                        size='xs'
                        rounded
                        style={{
                            backgroundColor: cursor.color,
                            borderRadius: '1000px'
                        }}
                        theme={{
                            root: {
                                base: 'relative',
                                initials: {
                                    base: 'flex flex-row items-center justify-center bg-inherit',
                                    text: 'text-white text-xs'
                                }
                            },
                        }}
                    />
                ))}
            </AvatarGroup>
        </div>
    );
};

const ShortcutListEntryEdit: React.FC<{
    entryKey: string;
    entry: DocumentShortcutState;
    readOnly: boolean;
    onUpdate: (key: string, entry: DocumentShortcutState) => void;
}> = ({
    entryKey,
    entry,
    readOnly,
    onUpdate,
}) => {
    const docEditor = useDocumentEditor();
    const { register, watch, formState: { errors }, getValues, setValue } = useForm<DocumentShortcutState>({
        defaultValues: {
            shortcut: entry.shortcut,
            meaning: entry.meaning
        },
    });

    const editorContents = useRef<Value>((entry.content ?? createSlateContents(entry.meaning ?? '')).children as Value); // Fallback to meaning due to back-compatibility

    const editor = useInlineEditor({
        document: docEditor.document,
        cursorData: docEditor.cursorData,
        initialValue: editorContents.current,
        addTrailingBlock: true
    });

    const updateEntry = useCallback(() => {
        const formValues = getValues();

        onUpdate(entryKey, {
            shortcut: formValues.shortcut ?? '',
            content: { children: editorContents.current }
        });
    }, [ editorContents, onUpdate, entryKey, getValues ]);

    useEffect(() => {
        const { unsubscribe } = watch(() => {
            updateEntry();
        });
        return () => unsubscribe();
    }, [ watch, updateEntry ]);

    useEffect(() => {
        if (entry.meaning) {
            setValue('meaning', null);
        }
    }, [ entry.meaning ]);

    const handleEditorValueChange = useCallback((value: Value) => {
        editorContents.current = value;
        updateEntry();
    }, [ editorContents ]);

    return (
        <div className='flex flex-col items-start gap-6 px-5 py-5 border-t bg-white border-gray-200'>
            <div>
                <Label htmlFor="shortcut" className="block mb-1">Název zkratky</Label>
                <TextInput
                    id="shortcut"
                    type="text"
                    sizing='md'
                    placeholder="NOVÁK Jan, 2025 - Jak psát studenstké práce..."
                    {...register('shortcut', {
                        required: 'Název je povinný',
                        maxLength: { value: 100, message: 'Max. délka je 100 znaků.' }
                    })}
                    readOnly={readOnly}
                    color={errors.shortcut ? 'failure' : 'default'}
                    helperText={(
                        <>
                            {errors.shortcut && (
                                <p>
                                    {errors.shortcut.message}
                                </p>
                            )}
                        </>
                    )}
                />
            </div>
            <div className='w-full'>
                <Label htmlFor="meaning" className="block mb-1">Vysvětlení zkratky</Label>
                <InlineEditor
                    editor={editor}
                    readOnly={readOnly}
                    onValueChange={handleEditorValueChange}
                    className='flex flex-col gap-0 overflow-hidden border border-gray-300 rounded-lg p-1'
                    containerClass='p-2 pt-3 flex-grow flex-shrink overflow-auto'
                    toolbarClass='bg-gray-100 p-1 w-full rounded-md overflow-y-hidden overflow-x-auto flex-shrink-0'
                />
            </div>
        </div>
    );
};
